import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import './GraphicTryoutPhotoFrame.css';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function GraphicTryoutPhotoFrame({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 120,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const {
    homeTeam,
    ageGroup,
    tryoutDate,
    tryoutDate2,
    tryoutTime,
    location,
    contact,
    additionalInfo,
    backgroundPhoto,
  } = graphicData;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;

  const accent = accentBlockColor || teamColors.primary;
  const detailColor = detailTextColor || '#ffffff';

  const getBackgroundStyle = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 0%, ${teamColors.primary} 50%, ${teamColors.secondary} 50%, ${teamColors.secondary} 100%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  return (
    <div className="graphic-tryout-photo-frame" id="graphic-tryout">
      {/* Background Photo */}
      <div className="tryout-photo-frame-background">
        {backgroundPhoto ? (
          <img
            src={backgroundPhoto}
            alt=""
            crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              maxWidth: 'none',
            }}
          />
        ) : (
          <div
            className="tryout-photo-frame-placeholder"
            style={{ background: getBackgroundStyle() }}
          >
            <div className="tryout-placeholder-icon"></div>
            <div className="tryout-placeholder-text">Upload team or field photo</div>
          </div>
        )}
        <div className="tryout-photo-frame-overlay" />
      </div>

      {/* Content Overlay */}
      <div className="tryout-photo-frame-content">
        {/* TRYOUTS Badge */}
        <div
          className="tryout-photo-frame-badge"
          style={{
            background: accent,
            fontFamily: heroFont,
            fontSize: heroFontSize * 0.18 + 'px',
          }}
        >
          TRYOUTS
        </div>

        {/* Team Section */}
        <div className="tryout-photo-frame-team-section">
          {homeTeamLogo && (
            <div className="team-logo-circle team-logo-circle-sm">
              <img
                src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
                alt={homeTeam}
                className="team-logo-img"
              />
            </div>
          )}
          <div className="tryout-photo-frame-team-info">
            <div className="tryout-photo-frame-team-name">
              {homeTeam || 'TEAM NAME'}
            </div>
            {ageGroup && (
              <div className="tryout-photo-frame-age-group">{ageGroup}</div>
            )}
          </div>
        </div>

        {/* Details Grid */}
        <div className="tryout-photo-frame-details-grid">
          <div className="tryout-detail-column">
            {tryoutDate && (
              <div className="tryout-photo-frame-detail-row">
                <div className="tryout-detail-content">
                  <div className="tryout-detail-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>DATE</div>
                  <div className="tryout-detail-value" style={{ color: detailColor }}>
                    {formatGameDate(tryoutDate, dateFormat)}
                    {tryoutDate2 && (
                      <> &amp; {formatGameDate(tryoutDate2, dateFormat)}</>
                    )}
                  </div>
                </div>
              </div>
            )}
            {tryoutTime && (
              <div className="tryout-photo-frame-detail-row">
                <div className="tryout-detail-content">
                  <div className="tryout-detail-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>TIME</div>
                  <div className="tryout-detail-value" style={{ color: detailColor }}>
                    {formatGameTime(tryoutTime)}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="tryout-detail-column">
            {location && (
              <div className="tryout-photo-frame-detail-row">
                <div className="tryout-detail-content">
                  <div className="tryout-detail-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>LOCATION</div>
                  <div className="tryout-detail-value" style={{ color: detailColor }}>
                    {location}
                  </div>
                </div>
              </div>
            )}
            {contact && (
              <div className="tryout-photo-frame-detail-row">
                <div className="tryout-detail-content">
                  <div className="tryout-detail-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>CONTACT</div>
                  <div className="tryout-detail-value" style={{ color: detailColor }}>
                    {contact}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Info */}
        {additionalInfo && (
          <div className="tryout-photo-frame-additional-info">
            <div className="tryout-info-text" style={{ color: detailColor }}>
              {additionalInfo}
            </div>
          </div>
        )}
      </div>

      {showWatermark && (
        <div className="playmaker-watermark">
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" />
        </div>
      )}
    </div>
  );
}

export default GraphicTryoutPhotoFrame;
